import React, { FC, useState } from 'react';
import { Blur } from 'ui';
import { Chip, Tooltip, Button } from '@mui/material';
import { useMessages } from '@scope/scope-one-ui/core';
import makeStyles from '@mui/styles/makeStyles';
import LockIcon from '@mui/icons-material/Lock';
import { requestRating } from 'modules/common/RestrictedSubscriptionRatings/services/rsrService';
import TrendIcon from 'modules/common/Table/TrendIcon';
import { useQuery } from 'shared/hooks';
import { useTranslation } from 'react-i18next';
import { outlookLabels } from 'shared/constants/outlookTypes';
import { formatCurrency, formatDate, setRatingColor } from 'shared/functions';
import { LinkButton } from 'ui/Buttons';
import { getName } from 'country-list';
import { OrangeRatingChip } from '../../../shared/styles/OrangeRatingChip/OrangeRatingChip';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});

export type RequestRatingButtonProps = {
  id: string;
  type?: string;
};

export const RequestRatingButton: FC<RequestRatingButtonProps> = ({ id, type }) => {
  const { success, error } = useMessages();
  const [pendingRequest, setPendingRequest] = useState(false);

  const handleRequest = () => {
    setPendingRequest(true);
    requestRating(id, type)
      .then(() => success({ content: 'Rating requested' }))
      .catch((err) =>
        error({
          title: err?.status || 'Error',
          content: err?.message || 'Unable to request rating',
        }),
      )
      .finally(() => setPendingRequest(false));
  };

  return (
    <Button
      size="small"
      variant="contained"
      disabled={pendingRequest}
      style={{ whiteSpace: 'nowrap' }}
      onClick={handleRequest}
    >
      Request
    </Button>
  );
};

export const useInstrumentRenderers = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { module } = useQuery();

  const nameRenderer = ([issueId, issueName, isBlurred]: string[]) =>
    isBlurred ? (
      <Blur />
    ) : (
      <LinkButton
        to={`instruments/${issueId}${module ? `?module=${module}` : ''}`}
        label={issueName}
        withPathname
      />
    );

  const ratingRenderer = (v: any) => {
    const [
      _,
      product,
      rating,
      outlook,
      hasLicence,
      ratingTrend,
      previousRating,
      previousOutlook,
      ratingDate,
      demandType,
      isBlurred,
      ratingId,
      notchSuffix,
      preliminaryFlag,
      ratingType,
    ] = v;

    if (isBlurred) {
      return <RequestRatingButton id={ratingId} type={ratingType} />;
    }

    if (rating) {
      const isUnsolicited = demandType?.includes('Unsolicited without participation');

      const ratingLabel = (
        <span>
          {preliminaryFlag && <span>(P) </span>}
          {rating}
          {notchSuffix && <sub> {notchSuffix}</sub>}
        </span>
      );

      const tooltip = (
        <>
          <div>
            {t('Rating')}: {ratingLabel}
          </div>
          <div>{outlook ? `${t('Outlook')}: ${t(outlookLabels[outlook])}` : ''}</div>
          {ratingTrend && (
            <>
              <div>
                {t('Previous rating')}: {previousRating}
              </div>
              <div>
                {t('Previous outlook')}: {previousOutlook}
              </div>
              <div>
                {t('Change as of latest rating action')}: {formatDate(ratingDate)}
              </div>
            </>
          )}
        </>
      );

      return (
        <Tooltip title={tooltip}>
          <div className={classes.wrapper}>
            {isUnsolicited ? (
              <OrangeRatingChip label={ratingLabel} variant="outlined" />
            ) : (
              <Chip label={ratingLabel} variant="outlined" color={setRatingColor(outlook)} />
            )}
            <TrendIcon trend={ratingTrend} />
          </div>
        </Tooltip>
      );
    }

    if (product) {
      return hasLicence ? 'N/A' : <LockIcon />;
    }

    if (!hasLicence) {
      return <LockIcon />;
    }

    return '';
  };

  const addedDateRenderer = ([value, isBlurred]) => {
    if (isBlurred) return <Blur />;

    return value ? formatDate(value, 'dd/MM/yyyy') : '-';
  };

  const entityRenderer = ([issuerId, issuerName, isBlurred]: string[]) =>
    isBlurred ? (
      <Blur />
    ) : (
      <LinkButton
        to={`/search-result/entities/${issuerId}${module ? `?module=${module}` : ''}`}
        label={issuerName?.toUpperCase()}
        color="primary"
      />
    );

  const countryRenderer = ([code, isBlurred]: string[]) => {
    if (isBlurred) return <Blur />;
    return code ? getName(code) : '';
  };

  const valueRenderer = (v: any) => {
    const [value, currencyCode, isBlurred] = v;

    if (isBlurred) return <Blur />;

    if (value != null) {
      return formatCurrency(value, currencyCode || 'EUR');
    }

    return '';
  };

  const typeRenderer = (type: string) => {
    if (type === 'ON_DEMAND_OPEN_SUBSCRIPTION') return 'SUBSCRIPTION';
    return type;
  };

  const protectedRenderer = ([value, isBlurred]) => (isBlurred ? <Blur /> : value);

  return {
    nameRenderer,
    ratingRenderer,
    addedDateRenderer,
    entityRenderer,
    countryRenderer,
    valueRenderer,
    typeRenderer,
    protectedRenderer,
  };
};
